import React, { useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { usePageChat } from '@/hooks';
import { ChatInput } from '../components/chat/ChatInput';
import { QuickSuggestionsBlock } from '../components/chat/QuickSuggestionsBlock';
import { Header } from '@components/layout';
import { Footer } from '@components/layout';
import { ChatContainer } from '../components/chat/ChatContainer';
import { LoadingState } from '../components/common/LoadingState';
import { SchemaMarkup } from '@/components/common/SchemaMarkup';
import { getVehicleByMakeModel } from '@/config/vehicles.config';
import { getComparisons } from '@/config/comparisons.config';

interface VehiclePageProps {
    showBanner: () => void;
}

export const VehiclePage: React.FC<VehiclePageProps> = ({ showBanner }) => {
    const { make, model } = useParams<{ make: string; model: string }>();
    const navigate = useNavigate();
    const resetKey = make && model ? `${make}/${model}` : undefined;
    const { messages, isLoading, streamingMessage, sendMessage, assistant, loadingAssistants, messagesEndRef, currentTool } = usePageChat(resetKey);
    const vehicle = make && model ? getVehicleByMakeModel(make, model) : undefined;
    const validComparisonSlugs = useMemo(() => new Set(getComparisons().map(c => c.slug)), []);

    // Loading state
    if (loadingAssistants || !assistant) {
        return (
            <>
                <Header />
                <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                    <div className="max-w-3xl mx-auto">
                        <LoadingState message="Loading..." />
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    // Vehicle not found
    if (!vehicle) {
        return (
            <>
                <Header />
                <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                    <div className="max-w-3xl mx-auto">
                        <div className="text-center text-white">
                            <h1 className="text-2xl font-bold mb-4">Vehicle Not Found</h1>
                            <p className="text-gray-400 mb-6">The vehicle you're looking for doesn't exist.</p>
                            <button
                                onClick={() => navigate('/vehicles')}
                                className="px-6 py-2 bg-cyan-400 hover:bg-cyan-500 text-black font-semibold rounded-full transition-all"
                            >
                                Browse Vehicles
                            </button>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    const vehicleSchema = vehicle ? {
        '@context': 'https://schema.org',
        '@type': 'Vehicle',
        name: `${vehicle.make} ${vehicle.model}`,
        manufacturer: {
            '@type': 'Organization',
            name: vehicle.make,
        },
        vehicleConfiguration: vehicle.vehicleType,
        description: vehicle.intro,
    } : null;

    const vehicleFaqSchema = vehicle ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: vehicle.researchTopics.map(topic => ({
            '@type': 'Question',
            name: topic,
            acceptedAnswer: {
                '@type': 'Answer',
                text: `Drivers researching ${topic.toLowerCase()} often compare the ${vehicle.make} ${vehicle.model} with other ${vehicle.vehicleType.toLowerCase()}s. The ${vehicle.make} ${vehicle.model} offers strong capability depending on engine and drivetrain configuration.`,
            },
        })),
    } : null;

    return (
        <>
            {vehicle && vehicleSchema && vehicleFaqSchema && (
                <SchemaMarkup schema={[vehicleSchema, vehicleFaqSchema]} />
            )}
            <Header />
            <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                <div className="max-w-3xl mx-auto">
                    <div className="min-h-[60vh] space-y-2 sm:space-y-3 md:space-y-4 py-2 sm:py-3 md:py-4">
                        <article className="mb-6">
                            {/* 1. H1 — split on em dash */}
                            {(() => {
                                const parts = vehicle.h1.split(' — ');
                                const name = parts[0];
                                const tagline = parts.slice(1).join(' — ');
                                return (
                                    <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-white mb-4 sm:mb-6">
                                        {name}
                                        {tagline && (
                                            <span className="block text-sm sm:text-base md:text-lg font-medium text-gray-400 mt-1">
                                                {tagline}
                                            </span>
                                        )}
                                    </h1>
                                );
                            })()}

                            {/* 2. Intro paragraph */}
                            <p className="text-sm sm:text-base text-gray-300 mb-4 sm:mb-6">
                                {vehicle.intro}
                            </p>

                            {/* 2b. What Is */}
                            {vehicle.whatIs && (
                                <div className="mb-4 sm:mb-6">
                                    <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                        What Is the {vehicle.make} {vehicle.model}?
                                    </h2>
                                    <p className="text-sm sm:text-base text-gray-300">
                                        {vehicle.whatIs}
                                    </p>
                                </div>
                            )}

                            {/* 3. Quick suggestion buttons */}
                            <QuickSuggestionsBlock
                                topLine="Not sure where to start?"
                                title={`Ask anything about the ${vehicle.make} ${vehicle.model}.`}
                                suggestions={vehicle.quickSuggestions}
                                onSend={sendMessage}
                            />

                            {/* 4. Quick Facts */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Quick Facts
                                </h2>
                                <div className="overflow-x-auto rounded-xl border border-gray-600">
                                    <table className="w-full text-sm text-gray-300">
                                        <thead>
                                            <tr className="bg-gray-800">
                                                <th className="text-left px-3 py-2 text-gray-400 font-medium w-1/3">Spec</th>
                                                <th className="text-left px-3 py-2 text-white font-semibold">{vehicle.make} {vehicle.model}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-700">
                                            {vehicle.quickFacts.map((fact, i) => (
                                                <tr key={i} className={i % 2 === 0 ? 'bg-[#1a1a1a]' : 'bg-gray-900'}>
                                                    <td className="px-3 py-2 text-gray-400">{fact.label}</td>
                                                    <td className="px-3 py-2 text-white">{fact.value}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* 5. About the [Make] [Model] */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    About the {vehicle.make} {vehicle.model}
                                </h2>
                                <p className="text-sm sm:text-base text-gray-300">
                                    {vehicle.about}
                                </p>
                                {vehicle.aboutList && vehicle.aboutList.length > 0 && (
                                    <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2 mt-2">
                                        {vehicle.aboutList.map((item, i) => (
                                            <li key={i}>{item}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {/* 6. Key Features */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Key Features
                                </h2>
                                {vehicle.features.map((section, i) => (
                                    <div key={i} className="mb-3">
                                        <h3 className="text-sm sm:text-lg font-semibold text-gray-200 mt-3 sm:mt-4 mb-1.5 sm:mb-2">
                                            {section.heading}
                                        </h3>
                                        <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2">
                                            {section.items.map((item, j) => (
                                                <li key={j}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>

                            {/* 7. Reliability and Ownership */}
                            {vehicle.reliability && (
                                <div className="mb-4">
                                    <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                        {vehicle.make} {vehicle.model} Reliability and Ownership
                                    </h2>
                                    <p className="text-sm sm:text-base text-gray-300">
                                        {vehicle.reliability}
                                    </p>
                                </div>
                            )}

                            {/* 8. Best For */}
                            {vehicle.bestFor && vehicle.bestFor.length > 0 && (
                                <div className="mb-4">
                                    <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                        What the {vehicle.make} {vehicle.model} Is Best For
                                    </h2>
                                    <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2">
                                        {vehicle.bestFor.map((item, i) => (
                                            <li key={i}>{item}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* 9. Vehicles Similar to the [Make] [Model] */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Vehicles Similar to the {vehicle.make} {vehicle.model}
                                </h2>
                                <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2">
                                    {vehicle.similarVehiclesLinked
                                        ? vehicle.similarVehiclesLinked.map((v, i) => (
                                            <li key={i}>
                                                <Link to={v.path} className="text-cyan-400 hover:text-cyan-300 underline transition-colors">
                                                    {v.name}
                                                </Link>
                                            </li>
                                        ))
                                        : vehicle.similarVehicles.map((v, i) => (
                                            <li key={i}>{v}</li>
                                        ))
                                    }
                                </ul>
                            </div>

                            {/* 9b. Compare to Other Vehicles */}
                            {vehicle.comparisons && vehicle.comparisons.length > 0 && (
                                <div className="mb-4">
                                    <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                        Compare the {vehicle.make} {vehicle.model} to Other {vehicle.vehicleType}s
                                    </h2>
                                    <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2">
                                        {vehicle.comparisons.map((c, i) => {
                                            const slug = c.path.replace('/compare/', '');
                                            const exists = validComparisonSlugs.has(slug);
                                            return (
                                            <li key={i}>
                                                {exists ? (
                                                    <Link to={c.path} className="text-cyan-400 hover:text-cyan-300 underline transition-colors">
                                                        {c.label}
                                                    </Link>
                                                ) : (
                                                    <span className="text-gray-500">{c.label}</span>
                                                )}
                                            </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}

                            {/* 10. Related Vehicles */}
                            {vehicle.relatedVehicleGroups.length > 0 && (
                                <div className="mb-4">
                                    <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                        Related Vehicles
                                    </h2>
                                    {vehicle.relatedVehicleGroups.map((group, i) => (
                                        <div key={i} className="mb-3">
                                            <h3 className="text-sm sm:text-lg font-semibold text-gray-200 mt-3 sm:mt-4 mb-1.5 sm:mb-2">
                                                {group.heading}
                                            </h3>
                                            <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2">
                                                {group.items.map((item, j) => (
                                                    <li key={j}>{item}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* 11. Dealerships That Sell the [Make] [Model] */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Dealerships That Sell the {vehicle.make} {vehicle.model}
                                </h2>
                                {vehicle.dealerships.length > 0 ? (
                                    <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2">
                                        {vehicle.dealerships.map((dealership, i) => (
                                            <li key={i}>
                                                <button
                                                    onClick={() => navigate(`/dealerships/${dealership.slug}`)}
                                                    className="text-cyan-400 hover:text-cyan-300 underline transition-colors"
                                                >
                                                    {dealership.name}
                                                </button>
                                                {' '}&mdash; {dealership.city}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-sm sm:text-base text-gray-300">
                                        Contact local dealerships to check availability near you.
                                    </p>
                                )}
                            </div>

                            {/* 12. Research Before Choosing */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Research {vehicle.vehicleType}s Before Choosing a Model
                                </h2>
                                <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2">
                                    {vehicle.researchTopicsLinked
                                        ? vehicle.researchTopicsLinked.map((t, i) => (
                                            <li key={i}>
                                                <Link to={t.path} className="text-cyan-400 hover:text-cyan-300 underline transition-colors">
                                                    {t.name}
                                                </Link>
                                            </li>
                                        ))
                                        : vehicle.researchTopics.map((topic, i) => (
                                            <li key={i}>{topic}</li>
                                        ))
                                    }
                                </ul>
                            </div>
                        </article>

                        {/* Follow-up Chat Messages */}
                        {messages.length > 0 && (
                            <ChatContainer
                                messages={messages}
                                assistant={assistant}
                                isLoading={isLoading}
                                streamingMessage={streamingMessage}
                                currentTool={currentTool}
                            />
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                </div>
            </div>
            <ChatInput
                assistant={assistant}
                onSendMessage={sendMessage}
                disabled={isLoading}
                showBanner={showBanner}
            />
        </>
    );
};
